import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { couponBenefitText } from '@euler/functions/apdm/couponRedemption';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmRecipientCouponCard,
  CouponBenefit,
  CouponBenefitType,
} from '@euler/model/apdm';
import dayjs from 'dayjs';
import { Fragment, memo } from 'react';
import { View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';

/** The big value on an item's right, and whether it is a giveaway. */
export function couponBenefitHeadline(benefit: CouponBenefit): {
  text: string;
  free: boolean;
} {
  switch (benefit.type) {
    case CouponBenefitType.PercentOff:
      return { text: `${(100 - benefit.percent) / 10}折`, free: false };
    case CouponBenefitType.CashOff:
      return { text: `减${benefit.amount}元`, free: false };
    case CouponBenefitType.FreeService:
    case CouponBenefitType.FreeItem:
      return { text: '免费', free: true };
    case CouponBenefitType.GiftOnPurchase:
      return { text: '赠品', free: true };
  }
}

/** The line under an item's name: what it applies to. */
export function couponBenefitScope(benefit: CouponBenefit): string {
  switch (benefit.type) {
    case CouponBenefitType.PercentOff:
    case CouponBenefitType.CashOff:
      return benefit.appliesTo?.length
        ? `适用：${benefit.appliesTo.join('、')}`
        : couponBenefitText(benefit);
    case CouponBenefitType.FreeService:
      return benefit.serviceName;
    case CouponBenefitType.FreeItem:
      return benefit.itemName;
    case CouponBenefitType.GiftOnPurchase:
      return couponBenefitText(benefit);
  }
}

/** The coupon poster a customer was sent, drawn as the coupon itself. */
export const ConversationCouponCard = memo(
  ({ coupon }: { coupon: ApdmRecipientCouponCard }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          width: '80%',
          maxWidth: rpx(320),
          borderRadius: rpx(18),
          overflow: 'hidden',
          backgroundColor: aiColors.muted,
          borderWidth: 1,
          borderColor: aiColors.border,
        }}
      >
        <View
          style={{
            paddingHorizontal: rpx(14),
            paddingVertical: rpx(12),
            backgroundColor: aiColors.foreground,
          }}
        >
          <Typography.Text
            bold
            size={rpx(14)}
            lineHeight={rpx(20)}
            color="#fff"
          >
            {coupon.title}
          </Typography.Text>
        </View>
        <View style={{ paddingHorizontal: rpx(14) }}>
          {coupon.items.map((item, index) => {
            const headline = couponBenefitHeadline(item.benefit);
            return (
              <Fragment key={`${item.label}:${index}`}>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: rpx(10),
                    paddingVertical: rpx(12),
                  }}
                >
                  <View style={{ flex: 1, gap: rpx(2) }}>
                    <Typography.Text
                      medium
                      size={rpx(12)}
                      color={aiColors.foreground}
                    >
                      {item.label}
                    </Typography.Text>
                    <Typography.Text
                      size={rpx(11)}
                      color={aiColors.mutedForeground}
                    >
                      {couponBenefitScope(item.benefit)}
                    </Typography.Text>
                  </View>
                  <Typography.Text
                    bold
                    size={rpx(16)}
                    color={
                      headline.free
                        ? aiColors.accentForeground
                        : aiColors.primary
                    }
                  >
                    {headline.text}
                  </Typography.Text>
                </View>
                <View style={{ height: 1, backgroundColor: aiColors.border }} />
              </Fragment>
            );
          })}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-end',
              gap: rpx(10),
              paddingVertical: rpx(12),
            }}
          >
            <View style={{ flex: 1, gap: rpx(3) }}>
              {[
                coupon.storeName,
                `有效期：${dayjs(coupon.startsAt).format(
                  'YYYY.MM.DD',
                )} — ${dayjs(coupon.endsAt).format('YYYY.MM.DD')}`,
                '到店出示二维码享优惠',
              ].map(line => (
                <Typography.Text
                  key={line}
                  size={rpx(10)}
                  color={aiColors.mutedForeground}
                >
                  {line}
                </Typography.Text>
              ))}
            </View>
            {coupon.qrUrl ? (
              <View
                style={{
                  padding: rpx(8),
                  borderRadius: rpx(12),
                  backgroundColor: '#fff',
                }}
              >
                <QRCode value={coupon.qrUrl} size={rpx(56)} />
              </View>
            ) : null}
          </View>
        </View>
      </View>
    );
  },
);
